import {
    Autocomplete,
    FormControl,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
    Paper,
    Collapse,
    Button,
} from "@mui/material";
import { AxiosError } from "axios";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    createSearchParams,
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import SearchEdit from "../components/inputs/SearchEdit";
import KlasisLoader from "../components/KlasisLoader";
import SearchResults from "../components/SearchResults";
import { ApiRoutes } from "../configuration/apiRoutes";
import { defaultLocale } from "../constants/languages";
import SEARCH_PARAMS from "../constants/searchParams";
import { Translations } from "../localization/translations";
import { FacetFilterItem } from "../models/FacetFilterItem";
import { SearchFacet } from "../models/SearchFacet";
import { SearchResponse } from "../models/SearchResponse";
import { SearchResponseOrder } from "../models/SearchResponseOrder";
import PageContent from "../parts/page-content/PageContent";
import { RestService } from "../utils/axios";
import useKlasisRouting from "../utils/klasisRouting";
import useKlasisTracker from "../utils/klasisTracker";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

interface IClassifiersFilter {
    pageNumber: number;
    searchPhrase?: string;
    sortOrder: SearchResponseOrder;
    facetsFilterValues: FacetFilterItem[];
}

const Classifiers: React.FC = () => {
    const [data, setData] = useState<SearchResponse>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [mobileFacetFormCollapsed, setMobileFacetFormCollapsed] =
        useState(true);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { klasisRoute } = useKlasisRouting();
    const { klasisTrackPageView, klasisTrackSiteSearch } = useKlasisTracker();

    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const search = useLocation().search;
    const isInitialMount = useRef(true);

    const { locale } = useParams();

    const [filter, setFilter] = useState<IClassifiersFilter>({
        sortOrder: SearchResponseOrder.weight,
        pageNumber: 1,
        facetsFilterValues: [],
    });
    const [searchPhrase, setSearchPhrase] = useState<string>("");

    const changeFacetItemFilterValue = (
        facetName: string,
        value: string | null
    ): void => {
        let facets = [...filter.facetsFilterValues];

        let existingFacetItem = facets.find(
            (facet) => facet.name === facetName
        );
        if (existingFacetItem) {
            if (!value) facets.splice(facets.indexOf(existingFacetItem), 1);
            else existingFacetItem.value = value ?? "";
        } else {
            facets.push({ name: facetName, value: value ?? "" });
        }

        setFilter({ ...filter, facetsFilterValues: facets, pageNumber: 1 });
    };

    const handleSortOrderChange = (order: SearchResponseOrder): void => {
        setFilter({ ...filter, sortOrder: order, pageNumber: 1 });
    };

    const handleSearch = (phrase: string): void => {
        setFilter({ ...filter, searchPhrase: phrase, pageNumber: 1 });
    };

    const handlePageChange = (page: number): void => {
        setFilter({ ...filter, pageNumber: page });
    };

    const getFacetFilterPrefix = (): string => `${SEARCH_PARAMS.facetFilter}-`;

    useEffect(() => {
        klasisTrackPageView();

        const searchParams = new URLSearchParams(search);

        const queryPhrase = searchParams.get(SEARCH_PARAMS.searchPhrase) ?? "";
        const queryPage = parseInt(searchParams.get(SEARCH_PARAMS.page) ?? "1");
        const queryOrder = parseInt(
            searchParams.get(SEARCH_PARAMS.order) ??
                SearchResponseOrder.weight.toString()
        );

        const queryFacetsFilterValues: FacetFilterItem[] = [];
        searchParams.forEach((value: string, key: string) => {
            if (!key.startsWith(`${SEARCH_PARAMS.facetFilter}-`)) return;

            queryFacetsFilterValues.push({
                name: key.substring(getFacetFilterPrefix().length),
                value: value,
            });
        });

        setFilter({
            searchPhrase: queryPhrase,
            pageNumber: queryPage,
            sortOrder: queryOrder,
            facetsFilterValues: queryFacetsFilterValues,
        });
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const refreshData = () => {
            setIsLoading(true);

            RestService.Get<SearchResponse>(
                ApiRoutes.Search.GetClassificationResults({
                    localeCode: locale ?? defaultLocale,
                    pageNumber: filter.pageNumber,
                    searchPhrase: filter.searchPhrase,
                    facets: filter.facetsFilterValues.map((item) => item.name),
                    facetValues: filter.facetsFilterValues.map(
                        (item) => item.value ?? ""
                    ),
                    sortOrder: filter.sortOrder,
                }),
                (response: SearchResponse) => {
                    setData(response);

                    if (searchPhrase) {
                        klasisTrackSiteSearch({
                            searchPhrase: searchPhrase,
                            requestId: response.requestId,
                        });
                    }
                },
                (error: AxiosError) => {
                    console.log("Error: " + error.message);
                },
                () => {
                    setIsLoading(false);
                }
            );
        };

        const query = new URLSearchParams();
        query.append(SEARCH_PARAMS.page, filter.pageNumber.toString());
        query.append(SEARCH_PARAMS.searchPhrase, filter.searchPhrase ?? "");
        query.append(SEARCH_PARAMS.order, filter.sortOrder.toString());

        filter.facetsFilterValues.forEach((item) => {
            if (item.value)
                query.append(
                    `${SEARCH_PARAMS.facetFilter}-` + item.name,
                    item.value
                );
        });

        navigate(
            {
                pathname: klasisRoute("classifications"),
                search: `?${createSearchParams(query)}`,
            },
            { replace: true }
        );

        refreshData();
    }, [filter, locale]);

    const renderSearchBox = (): JSX.Element => {
        return (
            <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                    <SearchEdit
                        placeholder={t(Translations.classifier.mainData.search)}
                        value={searchPhrase}
                        onSearch={handleSearch}
                        onChange={(event) => {
                            setSearchPhrase(event.target.value);
                        }}
                        onSeachClear={() => {
                            setSearchPhrase("");
                            handleSearch("");
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Select
                        size="small"
                        value={filter.sortOrder}
                        onChange={(e) =>
                            handleSortOrderChange(
                                typeof e.target.value === "number"
                                    ? e.target.value
                                    : SearchResponseOrder.weight
                            )
                        }
                        fullWidth
                    >
                        <MenuItem value={SearchResponseOrder.weight}>
                            {t(Translations.classifierList.sortOrder.weight)}
                        </MenuItem>
                        <MenuItem value={SearchResponseOrder.mostPopular}>
                            {t(
                                Translations.classifierList.sortOrder
                                    .mostPopular
                            )}
                        </MenuItem>
                        <MenuItem value={SearchResponseOrder.ascending}>
                            {t(Translations.classifierList.sortOrder.ascending)}
                        </MenuItem>
                        <MenuItem value={SearchResponseOrder.descending}>
                            {t(
                                Translations.classifierList.sortOrder.descending
                            )}
                        </MenuItem>
                    </Select>
                </Grid>

                {mdDown && (
                    <Grid item>
                        <Paper
                            variant="outlined"
                            sx={{
                                padding: mobileFacetFormCollapsed ? 0 : 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Collapse in={!mobileFacetFormCollapsed}>
                                {renderFacetForm()}
                            </Collapse>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setMobileFacetFormCollapsed((c) => !c)
                                }
                            >
                                {mobileFacetFormCollapsed ? (
                                    <ExpandMore />
                                ) : (
                                    <ExpandLess />
                                )}
                            </Button>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        );
    };

    const renderFacetForm = () => {
        return (
            <>
                {data?.facets.map((item) => (
                    <FormControl fullWidth key={`facet-${item.name}`}>
                        <Typography
                            variant="caption"
                            sx={{ marginBottom: ".5rem" }}
                        >
                            {t(getFacetLabel(item))}
                        </Typography>

                        <Autocomplete
                            size="small"
                            disablePortal
                            value={
                                filter.facetsFilterValues.find(
                                    (filterItem) =>
                                        filterItem.name === item.name
                                )?.value ?? null
                            }
                            sx={{ marginBottom: ".5rem" }}
                            onChange={(
                                _event: React.SyntheticEvent,
                                value: any
                            ) => {
                                changeFacetItemFilterValue(item.name, value);
                            }}
                            options={item.values ?? []}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t(
                                        Translations.classifierList.facet.all
                                    )}
                                />
                            )}
                        />
                    </FormControl>
                ))}
            </>
        );
    };

    const getFacetLabel = (item: SearchFacet): string => {
        return (Translations.classifierList.facet as any)[
            _.camelCase(item.name)
        ];
    };

    return (
        <PageContent
            title={t(Translations.pages.title.classifications)}
            showBreadcrumbs={true}
            showTitleBarCommands={true}
        >
            <Grid container spacing={1}>
                {!mdDown && <Grid item md={3} />}
                <Grid item xs={12} md={9}>
                    {renderSearchBox()}
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                {!mdDown && (
                    <Grid item md={3}>
                        {renderFacetForm()}
                    </Grid>
                )}

                <Grid item xs={12} md={9}>
                    {isLoading ? (
                        <KlasisLoader />
                    ) : (
                        <SearchResults
                            data={data}
                            onPageChange={handlePageChange}
                            searchPhrase={searchPhrase}
                        />
                    )}
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default Classifiers;
